import React, { useState } from 'react';
import * as Styles from './LinkNaBioStep3.styles';
import { IconDashboard, Button, IconCheck } from 'gatorcomponents';
import { Preview } from '../Preview';
import ProfileImageUpload from '../ProfileImageUpload';
import snappyDesktop from '@/media/siteBuilder/snappy-desktop.svg';
import snappyMobile from '@/media/siteBuilder/snappy-mobile.svg';
import { colorSchemes, templates } from '../../LinkNaBioWizardHandler.enum';
import withErrorBoundary from '@/utils/errorBoundary';
import { useDispatch } from 'react-redux';
import { linkNaBioActions, commonActions } from '@/redux/modules';
import { locale } from '@/utils/locale';

const templateTypes = Object.values(templates);
const colorSchemeTypes = Object.values(colorSchemes);

const LinkNaBioStep3 = ({
  testId = 'link-na-bio-step-3',
  prevStep = () => null,
  stepLocale = {},
  previewData = {
    links: [],
    title: '',
    description: '',
    emoji: undefined,
    socialMediaLinks: [],
    profileImage: null,
  },
  stepData = {
    colorScheme: colorSchemes.SIMPLE,
    template: templates.FUNNY,
    branding: true,
  },
  setStepData = () => null,
  handleNextStep = () => null,
  linkNaBioLoading = false,
  isEditing = false,
  setFieldUpdate = () => null,
  onMobile = false,
  profileId = null,
  profileImageData = {
    profileImage: null,
    profileImageFile: null,
  },
  setProfileImageData = () => null,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const dispatch = useDispatch();

  const safeStepData = {
    colorScheme: (stepData && stepData.colorScheme) || colorSchemes.SIMPLE,
    template: (stepData && stepData.template) || templates.FUNNY,
    branding: stepData && stepData.branding !== undefined ? stepData.branding : true,
  };

  const updateStepData = (name, value) => {
    const newStepData = { ...safeStepData };
    newStepData[name] = value;
    setStepData(newStepData);

    if (isEditing) {
      setFieldUpdate(true);
    }
  };

  const handleImageUpload = async (file, previewUrl) => {
    if (profileId && file) {
      dispatch(linkNaBioActions.profilePicture.upload.request({
        file,
        profileId,
      }));

      setProfileImageData({
        profileImage: previewUrl,
        profileImageFile: file,
      });

      if (isEditing) {
        setFieldUpdate(true);
      }

      await new Promise(resolve => setTimeout(resolve, 300));
    } else {
      setProfileImageData({
        profileImage: previewUrl,
        profileImageFile: file,
      });

      if (isEditing) {
        setFieldUpdate(true);
      }

      await new Promise(resolve => setTimeout(resolve, 300));
    }
  };

  const handleImageValidationError = (errorKey) => {
    dispatch(commonActions.notifications.set({
      label: locale(`linkNaBio.toasts.${errorKey}`),
      type: 'error',
    }));
  };

  const handleImageDelete = async () => {
    if (!profileId) {
      setProfileImageData({
        profileImage: null,
        profileImageFile: null,
      });

      if (isEditing) {
        setFieldUpdate(true);
      }

      await new Promise(resolve => setTimeout(resolve, 300));
      return;
    }

    setIsDeleting(true);

    dispatch(linkNaBioActions.profilePicture.delete.request({
      profileId,
    }));

    setProfileImageData({
      profileImage: null,
      profileImageFile: null,
    });

    if (isEditing) {
      setFieldUpdate(true);
    }

    await new Promise(resolve => setTimeout(resolve, 300));
    setIsDeleting(false);
  };

  const handleShowPreview = () => {
    setShowPreview(!showPreview);
  };

  return (
    <Styles.Wrapper data-testid={testId}>
      {(!onMobile || (onMobile && !showPreview)) && (
        <Styles.Content>
          <IconDashboard />
          <Styles.Title>
            {stepLocale.customize}
          </Styles.Title>

          <ProfileImageUpload
            locale={{
              profileImageLabel: stepLocale.profileImageLabel,
              addImageButton: stepLocale.addImageButton,
              changeImageButton: stepLocale.changeImageButton,
              removeImageButton: stepLocale.removeImageButton,
            }}
            profileImage={profileImageData.profileImage}
            onImageUpload={handleImageUpload}
            onImageDelete={handleImageDelete}
            onValidationError={handleImageValidationError}
            isDeleting={isDeleting}
            isUploading={linkNaBioLoading}
          />

          <Styles.Subtitle>
            {stepLocale.colorScheme}
          </Styles.Subtitle>

          <Styles.ColorForm>
            {colorSchemeTypes.map(colorScheme => (
              <Styles.ColorPill
                key={colorScheme}
                htmlFor={colorScheme}
                variant={colorScheme}
                onClick={() => updateStepData('colorScheme', colorScheme)}
                defaultChecked={safeStepData.colorScheme === colorScheme}
              >
                {safeStepData.colorScheme === colorScheme && (
                  <Styles.IconCheck pill>
                    <IconCheck color="primaryMedium" />
                  </Styles.IconCheck>
                )}
                <Styles.Radio
                  type="radio"
                  id={colorScheme}
                  name={colorScheme}
                  value={colorScheme}
                  defaultChecked={safeStepData.colorScheme === colorScheme}
                  data-testid={colorScheme}
                />
              </Styles.ColorPill>
            ))}
          </Styles.ColorForm>


          <Styles.Subtitle>
            {stepLocale.template}
          </Styles.Subtitle>


          <Styles.TemplateForm>
            {templateTypes.map(templateType => (
              <Styles.TemplateWrapper
                htmlFor={templateType}
                key={templateType}
                onClick={() => updateStepData('template', templateType)}
              >
                <Styles.TemplateCard
                  variant={safeStepData.colorScheme}
                  defaultChecked={safeStepData.template === templateType}
                >
                  {safeStepData.template === templateType && (
                    <Styles.IconCheck>
                      <IconCheck color="primaryBlue" />
                    </Styles.IconCheck>
                  )}
                  <Styles.Radio
                    type="radio"
                    id={templateType}
                    name={templateType}
                    value={templateType}
                    defaultChecked={safeStepData.template === templateType}
                    data-testid={templateType}
                  />
                  <Styles.TemplateHeader
                    template={templateType}
                  >
                    <Styles.TemplateRow variant={safeStepData.colorScheme} size="title" templateType={templateType} />
                    <Styles.TemplateRow variant={safeStepData.colorScheme} size="description" templateType={templateType} />
                  </Styles.TemplateHeader>

                  <Styles.TemplateBody>
                    <Styles.TemplateRow variant={safeStepData.colorScheme} size="row" templateType={templateType} />
                    <Styles.TemplateRow variant={safeStepData.colorScheme} size="row" templateType={templateType} />
                    <Styles.TemplateRow variant={safeStepData.colorScheme} size="row" templateType={templateType} />
                  </Styles.TemplateBody>

                </Styles.TemplateCard>
                <Styles.TemplateName>
                  {stepLocale[templateType]}
                </Styles.TemplateName>
              </Styles.TemplateWrapper>
            ))}
          </Styles.TemplateForm>

          <Styles.ButtonRow>
            <Styles.ButtonWrapper>
              <Button
                label={stepLocale.publish}
                size={onMobile ? 'medium' : 'large'}
                onClick={handleNextStep}
                loading={linkNaBioLoading}
              />
            </Styles.ButtonWrapper>

            {onMobile && (
              <Styles.ButtonWrapper>
                <Button
                  label={stepLocale.previewButton}
                  variant="secondary"
                  onClick={handleShowPreview}
                />
              </Styles.ButtonWrapper>
            )}
          </Styles.ButtonRow>

          <Styles.BackButton>
            <Button
              label={stepLocale.back}
              size="large"
              variant="tertiary"
              onClick={prevStep}
            />
          </Styles.BackButton>


        </Styles.Content>
      )}

      {(!onMobile || (onMobile && showPreview)) && (
        <Preview
          handleShowPreview={handleShowPreview}
          stepLocale={stepLocale}
          description={previewData.description}
          title={previewData.emoji ? `${previewData.emoji} ${previewData.title}` : previewData.title}
          links={previewData.links}
          variant={safeStepData.colorScheme}
          templateType={safeStepData.template}
          allowMobile
          onMobile={onMobile}
          socialMedia={previewData.socialMediaLinks}
          branding={safeStepData.branding}
          setStepData={updateStepData}
          setFieldUpdate={setFieldUpdate}
          profileImage={profileImageData.profileImage || previewData.profileImage}
        />
      )}
      <Styles.Snappy src={onMobile ? snappyMobile : snappyDesktop} alt="snappy" data-testid="snappy-image" />
    </Styles.Wrapper>
  );
};

export default withErrorBoundary(LinkNaBioStep3, 'LinkNaBioStep3');
